'use client';

import React, { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useIdleTimer } from 'react-idle-timer';
import Conditional from '../Conditional';
import DynamicModal from '../elements/DynamicModal/DynamicModal';
import {
  getSessionNumber,
  hideDynamicModal,
  showDynamicModal,
} from '@next-app/utils/common-utils';
import { removeFromSessionStorage } from '@next-app/utils/session-storage-utils';
import LLButton from '../elements/LLButton/LLButton';
import { useSelector } from 'react-redux';
import {
  selectDeviceType,
  selectDynSess,
  selectFeatureToggles,
  selectIsFurniture,
  selectIsLoggedIn,
  selectIsSessionInfoAvailable,
  selectSessionInfo,
} from '@next-app/lib/features/InitSelectors';
import { SessionExpiryMessages } from '@next-app/constants/CheckoutConstants';
import { invalidateSession } from '@next-app/utils/CheckoutHelper';
import { selectIsCheckoutDetailsLoaded } from '@next-app/lib/features/checkout/CheckoutSelector';
import { selectIsShoppingCartDataLoaded } from '@next-app/lib/features/ShoppingCart/ShoppingCartSelector';

/**
 * Component to show a modal when the user's session is about to expire.
 * Handles automatic session renewal, idle timeout, and redirecting upon expiration.
 */
const ExpiryModal: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isShoppingCartPage = pathname?.includes('/shopping-cart');

  // Redux selectors for session config and status
  const {
    firstSessionResetTime,
    maxSessionExpiryTime,
    sessionExpiryAlertTime,
  } = useSelector(selectSessionInfo) || {};
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isSessionInfoAvailable = useSelector(selectIsSessionInfoAvailable);
  const dnSessConf = useSelector(selectDynSess);
  const deviceType = useSelector(selectDeviceType);
  const isCheckoutDetailsLoaded =
    useSelector(selectIsCheckoutDetailsLoaded) || false;
  const isShoppingCartDataLoaded =
    useSelector(selectIsShoppingCartDataLoaded) || false;
  const { showSessionExpireModal = false } =
    useSelector(selectFeatureToggles) || {};
  const isFurniture = useSelector(selectIsFurniture);

  // UI state for modal and countdown
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState<number>(
    sessionExpiryAlertTime || 60,
  );
  const [phase, setPhase] = useState<'INITIAL' | 'POST_RENEWAL' | 'MODAL'>(
    'INITIAL',
  );

  const modalCountdownRef = useRef<any>(null);
  const renewalTimerRef = useRef<any>(null); // New ref for the renewal timer
  const isMobileView = deviceType === 'mobile';
  const expiredMessage = isLoggedIn
    ? SessionExpiryMessages.sessionExpiredSignedIn
    : SessionExpiryMessages.sessionExpiredGuest;

  /**
   * Starts a countdown timer (in seconds) and triggers session expiry when it reaches zero.
   */
  const resetModalCountdown = () => {
    if (modalCountdownRef.current) clearInterval(modalCountdownRef.current);
    setRemainingSeconds(sessionExpiryAlertTime);

    modalCountdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(modalCountdownRef.current!);
          // Avoid async race condition by deferring session expiry
          setTimeout(() => handleSessionExpiry(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000); // 1 second interval
  };

  /**
   * Renews the session by calling session endpoint and resets timers/modal state.
   */
  const continueSession = async () => {
    removeFromSessionStorage('llSessionExpiredInfo');
    removeFromSessionStorage('isSessionExpired');
    setShowModal(false);
    hideDynamicModal();
    clearInterval(modalCountdownRef.current!);
    setPhase('INITIAL');

    const success = await getSessionNumber(isShoppingCartPage);
    if (success) {
      console.log('Session continued via modal');
      reset(); // reset idle timer after renewal
    }
  };

  /**
   * Handles session expiration: closes modal, redirects user, and invalidates session.
   */
  const handleSessionExpiry = async () => {
    setShowModal(false);
    hideDynamicModal();
    console.log('Session expired - redirecting');

    if (showSessionExpireModal && !isMobileView) {
      sessionStorage.setItem('llSessionExpiredInfo', expiredMessage);
      sessionStorage.setItem('isSessionExpired', 'true');
    }
    router.push(isFurniture ? '/furniture' : '/'); // Redirect based on furniture flag
    removeFromSessionStorage('checkoutAsGuest');

    try {
      await invalidateSession(dnSessConf, isFurniture); // Invalidate session in background
    } catch (e) {
      console.error('Error invalidating session:', e);
    }
  };

  /**
   * Sets up a timer to auto-renew the session 2 seconds before max expiry time
   */
  const setupAutoRenewalTimer = () => {
    if (renewalTimerRef.current) clearTimeout(renewalTimerRef.current);

    // Calculate time for auto-renewal (2 seconds before max expiry)
    const renewalTime = (maxSessionExpiryTime - 2) * 1000;

    renewalTimerRef.current = setTimeout(async () => {
      console.log('Attempting auto-renewal 2 seconds before session expiry');
      const success = await getSessionNumber(isShoppingCartPage);
      if (success) {
        console.log('Session auto-renewed successfully');
        setPhase('POST_RENEWAL');
      }
    }, renewalTime);
  };

  /**
   * Idle timer that handles session renewal and session expiry.
   */
  const { reset } = useIdleTimer({
    timeout:
      phase === 'INITIAL'
        ? firstSessionResetTime * 1000
        : maxSessionExpiryTime * 1000,
    onIdle: async () => {
      if (phase === 'INITIAL') {
        console.log('Auto-renewing session after firstSessionResetTime');
        const success = await getSessionNumber(isShoppingCartPage);
        if (success) {
          setPhase('POST_RENEWAL');
          setupAutoRenewalTimer(); // Set up the auto-renewal timer
          reset(); // Restart timer with POST_RENEWAL phase
        }
      } else if (phase === 'POST_RENEWAL') {
        setPhase('MODAL');
        resetModalCountdown();

        if (showSessionExpireModal && !isMobileView) {
          console.log('Showing modal for session expiry');
          setMessage(SessionExpiryMessages.sessionAboutToExpire);
          setShowModal(true);
          showDynamicModal();
        }
      }
    },
    startOnMount: true,
    startManually: false,
    debounce: 0,
    events: [], // Custom tracking, no default browser events
    disabled: !isSessionInfoAvailable,
  });

  /**
   * On mount: if session expired flag is in storage, show the modal directly.
   */
  useEffect(() => {
    const sessionExpiredInfo = sessionStorage.getItem('llSessionExpiredInfo');
    if (
      sessionExpiredInfo &&
      (isCheckoutDetailsLoaded || isShoppingCartDataLoaded)
    ) {
      setMessage(sessionExpiredInfo);
      setShowModal(true);
      showDynamicModal();
    }
  }, [isCheckoutDetailsLoaded, isShoppingCartDataLoaded]);

  /**
   * Cleanup on unmount to clear any timers.
   */
  useEffect(() => {
    return () => {
      if (modalCountdownRef.current) clearInterval(modalCountdownRef.current);
      if (renewalTimerRef.current) clearTimeout(renewalTimerRef.current);
    };
  }, []);

  return (
    <Conditional if={showModal}>
      <DynamicModal
        onModalClose={async () => {
          setShowModal(false);
          hideDynamicModal();
        }}
        modalTitle="Session Expiry"
        modalContentClass="ll-small"
      >
        <div className="mb-3">
          <Conditional if={!!message}>
            <p className="mb-0" dangerouslySetInnerHTML={{ __html: message }} />
          </Conditional>

          <Conditional if={phase === 'MODAL'}>
            <p>{remainingSeconds}</p>
          </Conditional>
        </div>

        <div className="text-center">
          <LLButton
            type="button"
            variation="primary"
            data-bs-dismiss="modal"
            data-dismiss="dynamicModalWrapper"
            onClick={continueSession}
          >
            Continue
          </LLButton>
        </div>
      </DynamicModal>
    </Conditional>
  );
};

export default ExpiryModal;
